import React from 'react';
import { motion } from 'framer-motion';
import { Rocket, Handshake, FileSearch, Sparkles, TrendingUp } from 'lucide-react';
import { useMotionValue, useTransform, animate, useInView } from 'framer-motion';
import PartnersSection from '../PartnersSection/PartnersSection';

const Counter = ({ end, duration = 2.5 }: { end: number; duration?: number }) => {
    const count = useMotionValue(0);
    const rounded = useTransform(count, Math.round);
    const [display, setDisplay] = React.useState(0);
    const ref = React.useRef(null);
    const isInView = useInView(ref, { once: true, margin: "-100px" });

    React.useEffect(() => {
        if (isInView) {
            const controls = animate(count, end, { duration, ease: "circOut" });
            return controls.stop;
        }
    }, [isInView, end, duration, count]);

    React.useEffect(() => {
        return rounded.onChange((latest) => {
            setDisplay(latest);
        });
    }, [rounded]);

    return <motion.span ref={ref}>{display}</motion.span>;
}

const TrustSection = () => {
    return (
        <section className=" bg-white overflow-hidden relative">
            {/* BACKGROUND ARCHITECTURE: Mesh Gradient & Texture */}
            <div className="absolute inset-0 z-0 pointer-events-none">
                {/* Main Base Gradient */}
                <div className="absolute inset-0 bg-[#fafafa]" />

                {/* Mesh Glows */}
                <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-purple-100/40 blur-[120px] rounded-full mix-blend-multiply animate-pulse" style={{ animationDuration: '8s' }} />
                <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-blue-50/50 blur-[100px] rounded-full mix-blend-multiply animate-pulse" style={{ animationDuration: '6s', animationDelay: '1s' }} />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[700px] bg-purple-50/30 blur-[150px] rounded-[100%] rotate-12" />

                {/* Noise Texture Layer */}
                <div className="absolute inset-0 opacity-[0.03] mix-blend-overlay" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")` }} />

                {/* Subtle Grid Pattern Overlay */}
                <div className="absolute inset-0 opacity-[0.02]" style={{ backgroundImage: 'linear-gradient(#8B5CF6 1px, transparent 1px), linear-gradient(90deg, #8B5CF6 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
            </div>

            <div className="mx-auto px-4 lg:px-8 w-full max-w-7xl relative z-10">
                <div className="grid grid-cols-1 lg:grid-cols-2 items-center gap-12 lg:gap-20">

                    {/* LEFT COLUMN: Content */}
                    <div className="max-w-2xl text-left">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, ease: "easeOut" }}
                        >
                            <span className="text-[#8B5CF6] font-bold text-[10px] md:text-xs tracking-[0.3em] uppercase mb-5 block">
                                Visão Geral
                            </span>

                            <h2 className="text-[28px] md:text-[36px] lg:text-[44px] font-black text-slate-900 leading-[1.2] tracking-[-0.04em] mb-8 font-outfit">
                                CONHEÇA MAIS SOBRE A <br /><span className="text-[#8B5CF6]">LUKOS</span>
                            </h2>

                            <p className="text-base md:text-lg text-slate-500 font-medium mb-6 tracking-tight leading-relaxed">
                                Com mais de <span className="font-bold text-[#8B5CF6]">10 anos de experiência</span>, a LUKOS tecnologia transforma a gestão de postos de combustíveis e lojas de conveniência com soluções completas e personalizadas.
                            </p>

                            <p className="text-base md:text-lg text-slate-500 font-medium mb-12 tracking-tight leading-relaxed">
                                Nosso <span className="font-bold text-[#75a36c]">ERP eficiente</span> é a escolha de centenas de empresas, otimizando milhões de transações mensais com segurança e precisão.
                            </p>
                        </motion.div>

                        {/* Stats Grid */}
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, margin: "-50px" }}
                            variants={{
                                hidden: { opacity: 0 },
                                visible: {
                                    opacity: 1,
                                    transition: { staggerChildren: 0.2 }
                                }
                            }}
                            className="grid grid-cols-1 sm:grid-cols-3 gap-8 pt-8 border-t border-slate-200 flex justify-between p-12"
                        >
                            <motion.div variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}>
                                <h3 className="text-[32px] md:text-[40px] font-black text-[#75a36c] mb-1 flex items-baseline gap-1  justify-center">
                                    <Counter end={10} duration={2} />
                                </h3>
                                <p className="text-slate-500 text-[10px] font-bold uppercase tracking-wider">Anos no Mercado</p>
                            </motion.div>

                            <motion.div variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}>
                                <h3 className="text-[32px] md:text-[40px] font-black text-[#8B5CF6] mb-1 flex items-baseline gap-1">
                                    +<Counter end={1} duration={1.5} />
                                </h3>
                                <p className="text-slate-500 text-[10px] font-bold uppercase tracking-wider">MILHÃO <br /> Cupons / Mês</p>
                            </motion.div>

                            <motion.div variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}>
                                <h3 className="text-[32px] md:text-[40px] font-black text-[#75a36c] mb-1 flex items-baseline gap-1">
                                    <Counter end={350} duration={2.5} />
                                </h3>
                                <p className="text-slate-500 text-[10px] font-bold uppercase tracking-wider">MILHÕES <br /> Vendas / Mês</p>
                            </motion.div>
                        </motion.div>
                    </div>

                    {/* RIGHT COLUMN: Visual */}
                    <div className="relative w-full max-w-xl mx-auto lg:mx-0">
                        {/* Main Image Container */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.98, x: 30 }}
                            whileInView={{ opacity: 1, scale: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                            className="relative z-10"
                        >
                            <div className="rounded-[3rem] overflow-hidden shadow-[0_30px_60px_-15px_rgba(0,0,0,0.2)] border-[8px] border-white aspect-square group">
                                <img
                                    src="https://lukos.com.br/wp-content/uploads/2025/01/IMG_0885-1.png"
                                    alt="LUKOS Expert"
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[2000ms] ease-out "
                                />
                            </div>
                        </motion.div>

                    </div>


                </div>

            </div>
            <div className="w-full relative z-20 mt-12">
                <PartnersSection />
            </div>

        </section>
    );
};

export default TrustSection;
